import CheckCircle from "@assets/images/check-circle.svg";
import InfoIcon from "@assets/images/info.png";
import XCircle from "@assets/images/x-circle.svg";
import { Box } from "@material-ui/core";
import { SubmitFilterDataType } from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import OrderListFilter from "@saleor/changeOrderExport/components/OrderListFilter";
import OrderListTable from "@saleor/changeOrderExport/components/OrderListTable";
import {
  ExporOrderListSortField,
  exporOrderListUrl,
  ExporOrderListUrlQueryParams
} from "@saleor/changeOrderExport/urls";
import Container from "@saleor/components/Container";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import {
  ChangeOrderOrderStatus,
  useSapChangeOrderQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginationLocal from "@saleor/hooks/usePaginationLocal";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

export interface OrdersViewProps {
  params: ExporOrderListUrlQueryParams;
}

interface StyleProps {
  fullScreen?: boolean;
}

const useStyles = makeStyles<StyleProps>(
  _ => ({
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        visibility: props => (props.fullScreen ? "hidden" : "visible"),
        margin: 0,
        marginBottom: props => (props.fullScreen ? "0.4rem" : "1.2rem")
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    },
    searchBar: {
      display: props => (props.fullScreen ? "none" : "block")
    },
    flex: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }
  }),
  { name: "OrdersView" }
);

function SectionMessage(props) {
  const classes = useStyles({});
  return (
    <Box className={classes.flex} data-testid="info-message">
      <img src={props.icon} alt="info" />
      <Box ml={1}>
        <MyTypography
          variant="body3"
          style={{
            color: props.color
          }}
        >
          {props.message}
        </MyTypography>
      </Box>
    </Box>
  );
}

const OrdersView: React.FC<OrdersViewProps> = ({ params }) => {
  const intl = useIntl();
  const [
    { fullScreen, filterOrder: filterAppState },
    dispatchAppState
  ] = useAppState();
  const [filter, setFilter] = useState<SubmitFilterDataType>({});
  const [isSearched, setIsSearched] = useState<boolean>(false);

  const classes = useStyles({ fullScreen });
  const navigate = useNavigator();

  const { updateListSettings, settings } = useListSettings(
    ListViews.FILTER_ORDER
  );

  useEffect(() => {
    updateListSettings("rowNumber", defaultListSettings.FILTER_ORDER.rowNumber);

    // SEO-8570 : Clear search result
    const params = new URLSearchParams(location.search);
    const filtered = params.get("filtered") === "true";

    // filter store
    if (filterAppState && filtered) {
      setFilter(filterAppState);
      setIsSearched(true);
    }

    if (!filtered) {
      dispatchAppState({
        type: "setFilterOrder",
        payload: {
          filterOrder: null
        }
      });
    }
  }, []);

  const mapOrderStatusFilterSAP = () => {
    const _filter = { ...filter };
    const listOrderStatusSAP: ChangeOrderOrderStatus[] = [];
    if (_filter.status?.length) {
      _filter.status.forEach(stt => {
        const listEnumArr = Object.values(ChangeOrderOrderStatus);
        if (listEnumArr.includes(stt)) {
          listOrderStatusSAP.push(ChangeOrderOrderStatus[stt]);
        }
      });
      return listOrderStatusSAP;
    } else {
      return undefined;
    }
  };

  const filterSAP = useMemo(
    () => ({
      contractNo: filter.piNo || undefined,
      saleOrderNo: filter.eoNo || undefined,
      purchaseOrderNo: filter.poNo || undefined,
      status: filter.statusSap || undefined,
      orderStatus: mapOrderStatusFilterSAP(),
      bu: filter.bu,
      saleOrg: filter.company === "All" ? undefined : filter.company,
      soldTo: filter.soldTo ? [filter.soldTo] : [""],
      shipTo: filter.shipTo ? filter.shipTo.substr(0, 10) : undefined,
      createDate: filter.createDate || undefined,
      lastUpdateDate: filter.lastUpdate || undefined,
      channel: "30",
      sourceOfApp: filter?.sourceOfApp || undefined
    }),
    [filter]
  );

  const { data: dataSAP, loading } = useSapChangeOrderQuery({
    skip: !isSearched || !Object.keys(filter).length,
    displayLoader: true,
    variables: {
      filter: filterSAP
    }
  });

  const listOrderSort =
    useMemo(() => {
      const sortField = (field, asc) => (itemA, itemB) => {
        const valueA = itemA[field];
        const valueB = itemB[field];
        return asc
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      };

      const dateSort = asc => (itemA, itemB) =>
        (asc ? 1 : -1) *
        (+new Date(
          itemA.orderDate
            .split("/")
            .reverse()
            .join("/")
        ) -
          +new Date(
            itemB.orderDate
              .split("/")
              .reverse()
              .join("/")
          ));

      if (dataSAP?.sapChangeOrder) {
        const paramSort = getSortParams(params);
        const listToSort = [...dataSAP.sapChangeOrder];

        switch (paramSort.sort) {
          case ExporOrderListSortField.piNo:
          default:
            listToSort.sort(sortField("contractNo", paramSort.asc));
            break;
          case ExporOrderListSortField.eoNo:
            listToSort.sort(sortField("soNo", paramSort.asc));
            break;
          case ExporOrderListSortField.poNo:
            listToSort.sort(sortField("poNo", paramSort.asc));
            break;
          case ExporOrderListSortField.soldTo:
            listToSort.sort(sortField("soldToCode", paramSort.asc));
            break;
          case ExporOrderListSortField.createDate:
            listToSort.sort(dateSort(paramSort.asc));
            break;
        }

        return listToSort;
      }
      return [];
    }, [dataSAP?.sapChangeOrder, params]) || [];

  const {
    currentData,
    next,
    prev,
    toFirst,
    toLast,
    disableNext,
    disablePrev,
    lastestPageItemNumber
  } = usePaginationLocal(listOrderSort, settings.rowNumber);

  const handleSort = field => {
    navigate(
      exporOrderListUrl({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };

  useEffect(() => {
    toFirst();
  }, [settings?.rowNumber, filter]);

  const handleChangeFilter = (filterObj: SubmitFilterDataType) => {
    setFilter({ ...filterObj });
    if (!isEmpty(filterObj)) {
      setIsSearched(true);
      navigate(
        exporOrderListUrl({
          ...params,
          filtered: true
        }),
        { replace: true }
      );
    }
  };

  const message = useMemo(() => {
    if (loading) {
      return {
        icon: InfoIcon,
        message: intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        }),
        color: "#555555"
      };
    }
    if (!isSearched) {
      return {
        icon: InfoIcon,
        message: intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        }),
        color: "#555555"
      };
    }
    if (isSearched && !dataSAP?.sapChangeOrder?.length) {
      return {
        icon: XCircle,
        message: intl.formatMessage({
          defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
        }),
        color: "#FF2600"
      };
    }
    return {
      icon: CheckCircle,
      message: intl.formatMessage({
        defaultMessage: "พบรายการที่เกี่ยวข้อง"
      }),
      color: "#28A745"
    };
  }, [dataSAP?.sapChangeOrder?.length, isSearched, loading]);

  return (
    <Container>
      <PageHeader
        className={classes.pageHeader}
        title={intl.formatMessage({
          defaultMessage: "Change Order",
          description: "label"
        })}
      ></PageHeader>
      <OrderListFilter
        message={<SectionMessage {...message} />}
        handleChangeFilter={handleChangeFilter}
        setIsSearched={setIsSearched}
        listOrder={dataSAP?.sapChangeOrder}
        loading={loading}
      />

      {listOrderSort?.length || loading ? (
        <OrderListTable
          pageInfo={{
            hasPreviousPage: !disablePrev(),
            hasNextPage: !disableNext()
          }}
          data={currentData()}
          settings={settings}
          onUpdateListSettings={updateListSettings}
          totalCount={dataSAP?.sapChangeOrder?.length}
          latestPageItemNumber={lastestPageItemNumber}
          onPreviousFirstPage={toFirst}
          onNextLastPage={toLast}
          disabled={loading}
          onNextPage={next}
          onPreviousPage={prev}
          onRowClick={null}
          sort={getSortParams(params)}
          onSort={handleSort}
          filter={filter}
        />
      ) : (
        <></>
      )}
    </Container>
  );
};

export default OrdersView;
